import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { VfSessionStore } from "../utils/storage/storageUtils";

interface ScrollPosition {
  top: number;
  left: number;
}

const SAVE_THROTTLE_MS = 200;
const RESTORE_DURATION_MS = 1500;
const MODAL_SELECTOR = '[role="dialog"], [aria-modal="true"]';

// Only the first restorer for a key is active, so sibling tables that share one
// scroll container (e.g. kanban stages) never fight each other.
const activeKeys = new Set<string>();

const readPosition = (key: string): ScrollPosition | null => {
  const saved = VfSessionStore.get<ScrollPosition | null>(`scroll:${key}`, null);
  const isValid =
    saved && typeof saved.top === "number" && typeof saved.left === "number";
  return isValid ? saved : null;
};

const isScrollable = (element: HTMLElement): boolean => {
  const { overflowY } = window.getComputedStyle(element);
  const canOverflow = ["auto", "scroll", "overlay"].includes(overflowY);
  return canOverflow && element.scrollHeight - element.clientHeight > 4;
};

// The element that actually scrolls: the marker, an ancestor, or a descendant.
const findScrollElement = (marker: HTMLElement): HTMLElement | null => {
  if (isScrollable(marker)) return marker;
  for (let el = marker.parentElement; el; el = el.parentElement) {
    if (isScrollable(el)) return el;
  }
  for (const el of marker.querySelectorAll<HTMLElement>("*")) {
    if (isScrollable(el)) return el;
  }
  return null;
};

const isRelated = (
  node: EventTarget | null,
  marker: HTMLElement
): node is HTMLElement =>
  node instanceof HTMLElement &&
  (node === marker || node.contains(marker) || marker.contains(node));

/**
 * Saves and restores a section's scroll offset across unmount/remount and
 * refresh (per tab). Attach the returned ref to a stable element; the scrolling
 * element is detected at runtime. With no key the route is used, so every table
 * restores by default.
 */
export const useScrollRestoration = (scrollKey?: string) => {
  const { pathname } = useLocation();
  const key = scrollKey ?? pathname;

  // Marker is state (not a ref) so the effect re-runs once the element mounts -
  // e.g. after a table's loading shimmer is replaced.
  const [marker, setMarker] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!marker) return;
    if (!scrollKey && marker.closest(MODAL_SELECTOR)) return; // skip modals
    if (activeKeys.has(key)) return; // a sibling already owns this container
    activeKeys.add(key);

    const target = readPosition(key);
    const deadline = performance.now() + RESTORE_DURATION_MS;
    let frameId: number | null = null;
    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    let isRestoring = Boolean(target && (target.top > 0 || target.left > 0));

    const stopRestoring = () => {
      isRestoring = false;
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = null;
    };

    // Re-apply the saved offset each frame until it lands or the window expires
    // (handles rows that render after an async fetch).
    const restore = () => {
      if (!isRestoring || !target) return;
      const el = findScrollElement(marker);
      if (el) {
        el.scrollTop = target.top;
        el.scrollLeft = target.left;
        if (Math.abs(el.scrollTop - target.top) <= 1) return stopRestoring();
      }
      if (performance.now() >= deadline) return stopRestoring();
      frameId = requestAnimationFrame(restore);
    };

    // Persist the offset (throttled) whenever the user scrolls this section.
    const save = (event: Event) => {
      if (isRestoring || saveTimer || !isRelated(event.target, marker)) return;
      const el = event.target as HTMLElement;
      saveTimer = setTimeout(() => {
        saveTimer = null;
        VfSessionStore.set(`scroll:${key}`, {
          top: el.scrollTop,
          left: el.scrollLeft,
        });
      }, SAVE_THROTTLE_MS);
    };

    const options = { passive: true, capture: true } as const;
    // A real user gesture cancels an in-progress restore so it never fights them.
    document.addEventListener("wheel", stopRestoring, options);
    document.addEventListener("touchstart", stopRestoring, options);
    document.addEventListener("scroll", save, options);
    if (isRestoring) frameId = requestAnimationFrame(restore);

    return () => {
      activeKeys.delete(key);
      stopRestoring();
      if (saveTimer) clearTimeout(saveTimer);
      document.removeEventListener("wheel", stopRestoring, options);
      document.removeEventListener("touchstart", stopRestoring, options);
      document.removeEventListener("scroll", save, options);
    };
  }, [key, scrollKey, marker]);

  return setMarker;
};
